import React from "react";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  StepButton,
  Typography,
  LinearProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
} from "@mui/icons-material";

/**
 * PageNavigation component - displays progress and allows navigation between pages
 * @param {Object} props
 * @param {Array} props.pages - Array of page objects
 * @param {number} props.activePage - Current active page index
 * @param {Function} props.onPageClick - Callback when a page is clicked
 * @param {boolean} props.allowNavigation - Whether page navigation is allowed
 * @param {Object} props.progress - Overall progress information
 */
const PageNavigation = ({
  pages = [],
  activePage = 0,
  onPageClick,
  allowNavigation = true,
  progress = {},
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (pages.length === 0) {
    return null;
  }

  const handlePageClick = (pageIndex) => {
    if (allowNavigation && onPageClick) {
      onPageClick(pageIndex);
    }
  };

  const getPageIcon = (pageIndex, page) => {
    if (page.completed) {
      return (
        <CheckCircleIcon
          sx={{
            color: theme.palette.success.main,
            fontSize: isSmallMobile ? 20 : 24,
          }}
        />
      );
    }

    if (pageIndex === activePage) {
      return (
        <Box
          sx={{
            width: isSmallMobile ? 20 : 24,
            height: isSmallMobile ? 20 : 24,
            borderRadius: "50%",
            bgcolor: theme.palette.primary.main,
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: isSmallMobile ? "0.75rem" : "0.875rem",
            fontWeight: "bold",
          }}
        >
          {pageIndex + 1}
        </Box>
      );
    }

    return (
      <RadioButtonUncheckedIcon
        sx={{
          color: theme.palette.grey[400],
          fontSize: isSmallMobile ? 20 : 24,
        }}
      />
    );
  };

  const getPageLabel = (page, pageIndex) => {
    if (isSmallMobile) {
      return `${pageIndex + 1}`;
    }

    return (
      <Box>
        <Typography
          variant="body2"
          sx={{ fontWeight: pageIndex === activePage ? 600 : 400 }}
        >
          {page.title}
        </Typography>
        {/* <Typography variant="caption" color="text.secondary">
          {page.completedItems}/{page.totalItems} items
        </Typography> */}
      </Box>
    );
  };

  return (
    <Box sx={{ width: "100%", mb: 1 }}>
      {/* Page Navigation */}
      {!isMobile && (
        <Stepper
          activeStep={activePage}
          orientation={isMobile ? "vertical" : "horizontal"}
          sx={{
            "& .MuiStepConnector-root": {
              top: isSmallMobile ? 10 : 12,
            },
            "& .MuiStepConnector-line": {
              borderColor: theme.palette.grey[300],
              borderTopWidth: 2,
            },
            "& .Mui-completed .MuiStepConnector-line": {
              borderColor: theme.palette.success.main,
            },
            "& .Mui-active .MuiStepConnector-line": {
              borderColor: theme.palette.primary.main,
            },
          }}
        >
          {pages.map((page, index) => (
            <Step key={page.id} completed={page.completed}>
              {allowNavigation ? (
                <StepButton
                  onClick={() => handlePageClick(index)}
                  icon={() => getPageIcon(index, page)}
                  sx={{
                    "& .MuiStepLabel-root": {
                      cursor: "pointer",
                    },
                    "& .MuiStepLabel-label": {
                      fontSize: isSmallMobile ? "0.75rem" : "0.875rem",
                    },
                  }}
                >
                  {getPageLabel(page, index)}
                </StepButton>
              ) : (
                <StepLabel
                  StepIconComponent={() => getPageIcon(index, page)}
                  sx={{
                    "& .MuiStepLabel-label": {
                      color:
                        index === activePage
                          ? theme.palette.text.primary
                          : theme.palette.text.secondary,
                      fontWeight: index === activePage ? 600 : 400,
                    },
                  }}
                >
                  {getPageLabel(page, index)}
                </StepLabel>
              )}
            </Step>
          ))}
        </Stepper>
      )}

      {/* Current Page Info */}
      {pages[activePage] && (
        <Box
          sx={{
            mt: 3,
            p: 2,
            bgcolor: "white",
            borderRadius: 2,
            border: `1px solid ${theme.palette.grey[200]}`,
          }}
        >
          <Typography variant="h6" gutterBottom>
            {pages[activePage].title}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Page {activePage + 1} of {pages.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              •
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {pages[activePage].completedItems} of{" "}
              {pages[activePage].totalItems} items completed
            </Typography>
            {pages[activePage].completed && (
              <>
                <Typography variant="body2" color="text.secondary">
                  •
                </Typography>
                <Typography
                  variant="body2"
                  color="success.main"
                  sx={{ fontWeight: 600 }}
                >
                  ✓ Complete
                </Typography>
              </>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PageNavigation;
